import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import api from "../services/api";
import "../styles/Login.css";

/**
 * Connexion à l'administration, en deux étapes :
 *   1. courriel + mot de passe → l'application envoie un code à usage unique
 *   2. saisie du code → délivrance du jeton de session
 *
 * Le mot de passe seul ne suffit donc plus à ouvrir l'administration.
 */
const Login: React.FC = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [etape, setEtape] = useState<"identifiants" | "code">("identifiants");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [erreur, setErreur] = useState("");
  const [info, setInfo] = useState("");
  const [enCours, setEnCours] = useState(false);

  const soumettreIdentifiants = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur("");
    setEnCours(true);

    try {
      await api.post("/auth/login", { email, password });
      setInfo(`Un code à 6 chiffres vient d'être envoyé à ${email}. Il est valable 10 minutes.`);
      setEtape("code");
    } catch (err: any) {
      // Le serveur distingue les identifiants refusés de l'échec d'envoi du
      // code : afficher son message évite de chercher un courriel qui n'est
      // jamais parti.
      setErreur(err?.response?.data?.message || "Email ou mot de passe incorrect.");
    } finally {
      setEnCours(false);
    }
  };

  const soumettreCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setErreur("");
    setEnCours(true);

    try {
      const reponse = await api.post("/auth/verify-code", { email, code });
      login(reponse.data.access_token);
      navigate("/doctorants");
    } catch (err: any) {
      setErreur(err?.response?.data?.message || "Code invalide ou expiré.");
      setCode("");
    } finally {
      setEnCours(false);
    }
  };

  const recommencer = () => {
    setEtape("identifiants");
    setCode("");
    setPassword("");
    setErreur("");
    setInfo("");
  };

  return (
    <div className="container-login">
      <h2 className="title">Connexion Admin</h2>

      {erreur && <p className="error-message">{erreur}</p>}
      {etape === "code" && info && <p className="info-message">{info}</p>}

      {etape === "identifiants" ? (
        <form className="login-form" onSubmit={soumettreIdentifiants}>
          <input
            type="email"
            className="input-field"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
          />

          <input
            type="password"
            className="input-field"
            placeholder="Mot de passe"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
          />

          <button type="submit" className="btn" disabled={enCours}>
            {enCours ? "Envoi du code…" : "Se connecter"}
          </button>
        </form>
      ) : (
        <form className="login-form" onSubmit={soumettreCode}>
          <input
            type="text"
            className="input-field input-code"
            placeholder="000000"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            required
          />

          <button type="submit" className="btn" disabled={enCours || code.length !== 6}>
            {enCours ? "Vérification…" : "Valider le code"}
          </button>

          <button type="button" className="btn-lien" onClick={recommencer}>
            Revenir à l'identification
          </button>
        </form>
      )}
    </div>
  );
};

export default Login;
